import { useMemo, type ReactNode } from 'react';
import {
  Text,
  Flex,
  Markdown,
  DataTable,
  type IDataTableColDef,
  type IDataTableGridOptions,
  type IDataTableCellRendererParams,
  type IDataTableProps,
  Spinner,
  cn,
} from '@kognitos/lattice';
import { useTranslation } from 'react-i18next';
import { preprocessMarkdown } from '@/modules/idp/utils/markdown';
import { isNumericColumn } from '@/shared/utils';
import { useGridScrollEdges } from './use-grid-scroll-edges';

/**
 * Type for row data with dynamic string keys
 */
type TRowData = Record<string, string>;

/**
 * Props for VirtualizedTable component
 */
export interface IVirtualizedTableProps {
  /** Column headers. If empty, numbered headers (0, 1, 2, ...) are generated */
  headers: string[];
  /** Table data - array of rows, each row is an array of cell values */
  data: string[][];
  /** Optional CSS class name for the table container. Defaults to h-[600px] */
  className?: string;
  /** Optional test ID for testing */
  testId?: string;
  /** Message shown when the table is empty. Defaults to `common.empty`. */
  emptyMessage?: string;
  /** Row height, forwarded to the design system table. Defaults to `default`. */
  size?: IDataTableProps<TRowData>['size'];
  /**
   * Wrap cell text over as many lines as it needs (the default). When false a
   * row stays one line tall and overflowing text ellipsizes. Either way AG
   * Grid styles the cell itself, so neither case needs a `cellStyle`.
   */
  wrapText?: boolean;
  /** Minimum column width in pixels. Defaults to 100. */
  minColumnWidth?: number;
  /** Turns a raw header into the label shown in the column head. */
  headerFormatter?: (header: string) => string;
  /**
   * Renders cell content for a column that needs special treatment.
   * Returning `undefined` for a given cell defers to the default: the value
   * rendered as markdown.
   */
  renderCell?: (params: { header: string; value: string }) => ReactNode;
  /**
   * Right-align the cells of columns whose values are all numeric, so digits
   * line up under their label. The values decide, not the column name, and
   * the label itself stays where every other label is.
   */
  alignNumericColumns?: boolean;
  /**
   * At or below this many rows the grid sizes itself to its content and the
   * container drops `className`'s height, so a three-row table stops claiming
   * a page-tall box. Omit to always keep the fixed height: auto height renders
   * every row into the DOM, which gives up row virtualization.
   */
  autoHeightRowLimit?: number;
  /**
   * Shade the right and bottom edges while there is more table to scroll to
   * that way. No shade means no scroll.
   */
  scrollFade?: boolean;
}

/**
 * Presentation options a caller can hand down through a renderer, without that
 * renderer having to know what any of them are for.
 */
export type TVirtualizedTableOptions = Omit<
  IVirtualizedTableProps,
  'headers' | 'data' | 'testId' | 'className' | 'emptyMessage'
>;

/**
 * Minimum column width in pixels to ensure content is readable
 */
const MIN_COLUMN_WIDTH = 100;

// No `autoSizeStrategy: 'fitCellContents'` — column widths use `flex` (below)
// so they fill the container instead of shrinking to content (ENG-2781).
const gridOptions: IDataTableGridOptions<TRowData> = {
  suppressColumnVirtualisation: true,
};

const autoHeightGridOptions: IDataTableGridOptions<TRowData> = {
  ...gridOptions,
  domLayout: 'autoHeight',
};

/**
 * VirtualizedTable component for efficiently rendering large tabular data
 *
 * Uses DataTable from lattice (built on AG Grid) which provides:
 * - Built-in row virtualization for optimal performance
 * - Sticky headers that stay visible during scroll
 * - Consistent design system styling
 *
 * If headers array is empty, numbered headers (0, 1, 2, ...) are generated based on data columns.
 *
 * @param props - Component props
 * @param props.headers - Array of column header strings (if empty, numbered headers are generated)
 * @param props.data - 2D array of cell values (rows x columns)
 * @param props.className - Optional CSS class name for the table container (default: h-[600px])
 * @param props.testId - Optional test ID
 *
 * @example
 * ```tsx
 * <VirtualizedTable
 *   headers={['Name', 'Email', 'Age']}
 *   data={[
 *     ['John', 'john@example.com', '30'],
 *     ['Jane', 'jane@example.com', '25'],
 *   ]}
 *   className="h-[400px]"
 * />
 * ```
 */
export function VirtualizedTable({
  headers: headersProp,
  data: dataProp,
  className = 'h-[600px]',
  testId = 'virtualized-table',
  emptyMessage,
  size = 'default',
  wrapText = true,
  minColumnWidth = MIN_COLUMN_WIDTH,
  headerFormatter,
  renderCell,
  alignNumericColumns = false,
  autoHeightRowLimit,
  scrollFade = false,
}: IVirtualizedTableProps) {
  const { t } = useTranslation();
  const { containerRef, gridEvents, edges } = useGridScrollEdges<TRowData>();

  /**
   * If headers are empty, generate numbered headers (0, 1, 2, ...) based on data columns.
   * Always densify: callers (e.g. ExcelJS) may pass sparse arrays, and sparse
   * arrays propagate through `.map`, producing undefined column defs that
   * crash AG Grid in `isColumnGroupDef`.
   */
  const headers = useMemo(() => {
    if (headersProp.length > 0) {
      return Array.from(
        { length: headersProp.length },
        (_, i) => headersProp[i] ?? '',
      );
    }
    const columnCount = dataProp[0]?.length ?? 0;
    return Array.from({ length: columnCount }, (_, i) => String(i));
  }, [headersProp, dataProp]);

  /** Which columns hold quantities, judged from the values they actually hold. */
  const numericColumns: boolean[] = useMemo(() => {
    if (!alignNumericColumns) {
      return [];
    }
    return headers.map((_, index) =>
      isNumericColumn(dataProp.map(row => row[index] ?? '')),
    );
  }, [alignNumericColumns, headers, dataProp]);

  /**
   * AG Grid column defs. Index-based `field` handles duplicate headers.
   * `flex: 1` fills the container; `minWidth` keeps columns readable and lets
   * wide tables scroll horizontally instead of shrinking below it (ENG-2781).
   */
  const columns: IDataTableColDef<TRowData>[] = useMemo(() => {
    return headers.map((header, index) => {
      const numeric = numericColumns[index] ?? false;
      return {
        field: `col_${index}`,
        // AG Grid only substitutes a humanised `field` ("Col 0") when
        // `headerName` is null, so an unnamed column keeps an empty head.
        headerName: headerFormatter ? headerFormatter(header) : header,
        // Only the cells: `ag-right-aligned-header` would flip the column's
        // label too, and a label reads from the same edge as every other.
        cellClass: numeric ? 'ag-right-aligned-cell' : undefined,
        flex: 1,
        minWidth: minColumnWidth,
        resizable: true,
        // Text that ellipsizes still has to be readable in full somewhere.
        tooltipField: `col_${index}`,
        // Toggles AG Grid's own wrapping; off, cells stay single-line with an ellipsis.
        wrapText,
        autoHeight: wrapText,
        cellRenderer: (params: IDataTableCellRendererParams<TRowData>) => {
          const value = String(params.value ?? '');
          if (!value) return null;
          const custom = renderCell?.({ header, value });
          return (
            // AG Grid centers inline content via line-height, not flex; block-level renderers need this explicit center.
            <Flex align="center" height="full" className="w-full min-w-0">
              {custom !== undefined ? (
                custom
              ) : (
                // Preserve real newlines as <br>; collapsed white-space would otherwise merge them.
                <Markdown
                  breaks
                  textProps={{ level: 'small', color: 'default' }}
                  className="w-full min-w-0"
                  textClassName={wrapText ? undefined : 'truncate'}
                >
                  {preprocessMarkdown(value)}
                </Markdown>
              )}
            </Flex>
          );
        },
      };
    });
  }, [
    headers,
    headerFormatter,
    renderCell,
    numericColumns,
    minColumnWidth,
    wrapText,
  ]);

  /**
   * Convert 2D array data to array of objects with column keys
   */
  const rowData: TRowData[] = useMemo(() => {
    return dataProp.map(row => {
      const rowObj: TRowData = {};
      headers.forEach((_, index) => {
        rowObj[`col_${index}`] = row[index] ?? '';
      });
      return rowObj;
    });
  }, [dataProp, headers]);

  // Empty state
  if (headers.length === 0 || dataProp.length === 0) {
    return (
      <Flex direction="column" gap={2} padding={4}>
        <Text level="small" color="muted">
          {emptyMessage ?? t('common.empty')}
        </Text>
      </Flex>
    );
  }

  // A short table shrink-wraps, so whatever holds it can do the same.
  const fitsContent =
    autoHeightRowLimit !== undefined && rowData.length <= autoHeightRowLimit;

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative w-full overflow-auto [--background:transparent]',
        fitsContent ? 'h-auto' : className,
      )}
      data-testid={testId}
    >
      <DataTable<TRowData>
        data={rowData}
        columns={columns}
        gridOptions={
          scrollFade
            ? {
                ...(fitsContent ? autoHeightGridOptions : gridOptions),
                ...gridEvents,
              }
            : fitsContent
              ? autoHeightGridOptions
              : gridOptions
        }
        className={cn('w-full', !fitsContent && 'h-full')}
        size={size}
        loadingOverlayComponent={() => <Spinner size="xl" />}
      />
      {scrollFade && edges.right && (
        <div
          aria-hidden
          // Same dissolve as the chat edges (`ChatEdgeFade`), into the sheet's own background.
          className="pointer-events-none absolute inset-y-0 right-0 z-10 w-8 bg-gradient-to-l from-card to-transparent"
          data-testid="table-scroll-fade-right"
        />
      )}
      {scrollFade && edges.bottom && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-8 bg-gradient-to-t from-card to-transparent"
          data-testid="table-scroll-fade-bottom"
        />
      )}
    </div>
  );
}
